import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NextLocator } from '@grupo-constant/next-dtype/types';
import { TranslocoModule } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { SharedModule } from '../../shared/shared.module';
import { NextBrand, NextCustomer, NextProject } from '../../modules/admin/projects/projects.types';
import { NextServicio } from '../../modules/admin/services/services.types';
import { DTYPE_BRAND_URI, DTYPE_CUSTOMER_URI, DTYPE_PROJECT_URI, ProjectsService } from '../../modules/admin/projects/projects.service';
import { SERVICE_DTYPE_URI } from '../../modules/admin/services/services.service';

@Component({
    selector: 'app-select-locator-dialog',
    templateUrl: './select-locator-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .select-locator-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SharedModule, TranslocoModule]
})
export class SelectLocatorDialogComponent implements OnInit {

    nodeMap: Map<string, (NextBrand | NextCustomer | NextProject | NextServicio)[]>;
    brands: NextBrand[] = [];
    customers: NextCustomer[] = [];
    projects: NextProject[] = [];
    services: NextServicio[] = [];

    formGroup = new FormGroup({
        clienteCode: new FormControl<number>(undefined),
        marcaCode: new FormControl<string>(undefined, Validators.required),
        projectId: new FormControl<string>(undefined),
        serviceId: new FormControl<string>(undefined),
    });

    private _unsubscribeAll = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
           marca_code?: string,
        },
        public dialogRef: MatDialogRef<SelectLocatorDialogComponent>,
        private projectsService: ProjectsService
    ) { }

    async ngOnInit() {
        this.nodeMap = await this.projectsService.retrieveVisibilityMap();
        if (this.data.marca_code) {
            this.formGroup.get('marcaCode').setValue(this.data.marca_code);
        } else {
            this.brands = this.nodeMap.get(DTYPE_BRAND_URI) as NextBrand[];
        }

        this.formGroup.get('marcaCode').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(marcaCode => this.marcaChanged(marcaCode));

        this.formGroup.get('clienteCode').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(clienteCode => this.clienteChanged(clienteCode));

        this.formGroup.get('projectId').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(projectId => this.projectChanged(projectId));
    }

    closeDialog() {
        this.dialogRef.close();
    }

    accept() {
        const locator: NextLocator = {
            OU: '01',
            marca_code: this.formGroup.get('marcaCode').value,
            cliente_code: this.formGroup.get('clienteCode').value,
            projectId: this.formGroup.get('projectId').value,
            serviceId: this.formGroup.get('serviceId').value
        };
        this.dialogRef.close(locator);
    }

    private marcaChanged(marcaCode: string) {
        if (marcaCode) {
            this.customers = (this.nodeMap.get(DTYPE_CUSTOMER_URI) as NextCustomer[]).filter(customer => customer.marca_code === marcaCode);
        } else {
            this.customers = [];
        }
        this.formGroup.get('clienteCode').setValue(undefined, { emitEvent: false });
        this.projects = [];
        this.formGroup.get('projectId').setValue(undefined, { emitEvent: false });
        this.services = [];
        this.formGroup.get('serviceId').setValue(undefined, { emitEvent: false });
    }

    private clienteChanged(clienteCode: number) {
        if (clienteCode !== undefined) {
            this.projects = (this.nodeMap.get(DTYPE_PROJECT_URI) as NextProject[]).filter(project => project.cliente_code === clienteCode);
        } else {
            this.projects = [];
        }
        this.formGroup.get('projectId').setValue(undefined, { emitEvent: false });
        this.services = [];
        this.formGroup.get('serviceId').setValue(undefined, { emitEvent: false });
    }

    private projectChanged(projectId: string) {
        if (projectId) {
            this.services = (this.nodeMap.get(SERVICE_DTYPE_URI) as NextServicio[]).filter(service => service.projectId === projectId);
        } else {
            this.services = [];
        }
        this.formGroup.get('serviceId').setValue(undefined, { emitEvent: false });
    }
}
